import { patientIdSchema } from "@repo/api/schemas";
import { useExamAdministrations, usePerformanceMeasures, useQuestionnaireInstances } from "@repo/data-store";
import { EmptyState } from "@repo/ui/components/empty-state";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ClipboardList } from "lucide-react";
import { useMemo } from "react";
import {
  AssessmentRail,
  buildAssessmentRail,
  examRailEntry,
  performanceMeasureRailEntry,
  questionnaireRailEntry,
} from "@/features/assessments";

export const Route = createFileRoute("/patients/$patientId/_layout/assessments")({
  component: RouteComponent,
});

function RouteComponent() {
  const patientId = patientIdSchema.parse(Route.useParams().patientId);
  const instances = useQuestionnaireInstances({ patientId });
  const exams = useExamAdministrations({ patientId });
  const measures = usePerformanceMeasures({ patientId });
  const groups = useMemo(
    () =>
      buildAssessmentRail([
        ...instances.map((instance) => questionnaireRailEntry(patientId, instance)),
        ...exams.map((exam) => examRailEntry(patientId, exam)),
        ...measures.map((measure) => performanceMeasureRailEntry(patientId, measure)),
      ]),
    [patientId, instances, exams, measures],
  );

  if (groups.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <EmptyState className="m-auto p-8">
          <EmptyState.Icon>
            <ClipboardList className="size-4" />
          </EmptyState.Icon>
          <EmptyState.Content>
            <EmptyState.Title>No assessments yet</EmptyState.Title>
            <EmptyState.Description>
              Assigned questionnaires, recorded exams and performance measures will appear here.
            </EmptyState.Description>
          </EmptyState.Content>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1">
      <AssessmentRail groups={groups} />
      <div className="min-w-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
